import Link from 'next/link';
import React, {useEffect, useState} from 'react';
import {useDebounce} from 'react-use';

import Button from '@src/components/atoms/Button';
import FormErrorMessage from '@src/components/atoms/FormErrorMessage';
import TextField from '@src/components/atoms/TextField';
import {
  AuthForm,
  AuthFormTitle,
  AuthFormWrapper,
  AuthFromSection,
  FooterOptionText,
} from '@src/components/forms/AuthForm';
import useAuth from '@src/hooks/useAuth';
import {signInFormValidate, ValidateField} from '@src/utils/validations';

import * as S from './SignInForm.styles';

const TWO_SECONDS = 2000;

const SignInForm = () => {
  const {logIn} = useAuth();

  const [signInError, setSignInError] = useState<string | undefined>(undefined);
  const [validatedFields, setValidatedFields] = useState<ValidateField>({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleValidateFields = () => {
    const validation = signInFormValidate(formData);
    setValidatedFields(validation);
  };

  const handleInputChange = (value: string, field: string) => {
    setFormData(previousValues => ({
      ...previousValues,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSignInError(undefined);

    try {
      await logIn(formData);
    } catch (error) {
      console.error(error);
      setSignInError('Incorrect email or password.');
    }
  };

  const [, cancel] = useDebounce(handleValidateFields, TWO_SECONDS, [formData]);

  useEffect(() => {
    cancel();
  }, [cancel]);

  return (
    <AuthFormWrapper>
      <AuthFormTitle>Sign In</AuthFormTitle>

      {signInError && <FormErrorMessage message={signInError} />}

      <AuthForm onSubmit={handleSubmit}>
        <AuthFromSection>
          <TextField
            label="Email"
            name="email"
            inputMode="email"
            error={validatedFields?.email?.error}
            isValid={validatedFields?.email?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            required
          />
          <S.TextFieldWithTextWarpper>
            <TextField
              label="Password"
              name="password"
              type="password"
              error={validatedFields?.password?.error}
              isValid={validatedFields?.password?.isValid}
              onChange={handleInputChange}
              onBlur={handleValidateFields}
              required
            />
            <S.ForgotPasswordLink>
              <Link href="/forgot-password" passHref>
                <a>Forgot Password?</a>
              </Link>
            </S.ForgotPasswordLink>
          </S.TextFieldWithTextWarpper>
        </AuthFromSection>

        <AuthFromSection>
          <Button type="submit" fullWidth disabled={!formData?.email || !formData.password}>
            Sign In
          </Button>
          <FooterOptionText>
            Don&apos;t have an account yet?{' '}
            <Link href="/signup" passHref>
              <a>Create an account.</a>
            </Link>
          </FooterOptionText>
        </AuthFromSection>
      </AuthForm>
    </AuthFormWrapper>
  );
};

export default SignInForm;
